import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, TrendingUp, AlertTriangle, CalendarDays, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { lvl } from "@/lib/levels";

const HORIZONS = [30, 90, 180, 365];

const Bar = ({ pct, level, label, sub }) => {
  const s = lvl(level);
  return (
    <div className="flex items-center gap-3">
      <div className="w-40 shrink-0 text-sm text-slate-700">{label}</div>
      <div className={`h-6 flex-1 overflow-hidden rounded-full ${s.track}`}>
        <div className={`flex h-full items-center justify-end rounded-full ${s.bar} px-2 transition-all duration-700`} style={{ width: `${Math.min(100, pct)}%` }}>
          <span className="text-[11px] font-semibold text-white">{Math.round(pct)}%</span>
        </div>
      </div>
      <div className="w-28 shrink-0 text-right text-xs text-slate-500">{sub}</div>
    </div>
  );
};

const Holidays = ({ drivers }) =>
  drivers?.length ? (
    <span className="ml-2 inline-flex flex-wrap gap-1">
      {drivers.map((d, i) => (
        <span key={d.name || i} title={d.name} className="rounded-full bg-slate-100 px-1.5 text-xs">{d.emoji}</span>
      ))}
    </span>
  ) : null;

export default function AdminForecast() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      setData((await api.get("/admin/forecast", { params: { days } })).data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [days, isAdmin]);

  useEffect(() => { load(); }, [load]);

  if (!isAdmin)
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <p className="mx-auto max-w-3xl px-5 py-24 text-slate-600" data-testid="forecast-no-access">{t("admin.noAccess")}</p>
      </div>
    );

  const peak = data?.peak_day;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-6xl px-5 py-12 lg:px-10">
        <Link to="/admin" data-testid="forecast-back-btn" className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors duration-200 hover:text-sea">
          <ArrowLeft size={15} /> {t("admin.title")}
        </Link>

        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-eyebrow">{t("admin.title")}</p>
            <h1 className="mt-2 flex items-center gap-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="forecast-title">
              <TrendingUp size={22} className="text-sea" /> {t("fc.forecast")}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <select data-testid="forecast-horizon-select" value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              {HORIZONS.map((h) => <option key={h} value={h}>{h} {t("fc.days")}</option>)}
            </select>
            <Button variant="outline" data-testid="forecast-refresh-btn" onClick={load}>
              <RefreshCw size={15} className="mr-1.5" /> {t("common.refresh")}
            </Button>
            <Link to="/admin/calendar" data-testid="to-calendar-link">
              <Button className="bg-sea hover:bg-sea-dark"><CalendarDays size={15} className="mr-1.5" /> {t("fc.calendar")}</Button>
            </Link>
          </div>
        </div>

        {loading && !data && <p className="mt-10 text-slate-500" data-testid="forecast-loading">{t("common.loading")}</p>}

        {data && (
          <div className="mt-8 space-y-10" data-testid="forecast-content">
            {/* KPIs */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="forecast-kpi-fleet">
                <p className="text-xs text-slate-500">{t("fc.fleet")}</p>
                <p className="mt-1.5 font-display text-3xl text-slate-900">{data.fleet_total}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="forecast-kpi-peak-util">
                <p className="text-xs text-slate-500">{t("fc.peakUtil")}</p>
                <p className="mt-1.5 font-display text-3xl text-slate-900">{data.peak_planning_utilisation_pct}%</p>
              </div>
              {peak && (
                <div className={`rounded-xl border p-5 ${lvl(peak.level).chip}`} data-testid="forecast-kpi-peak-day">
                  <p className="flex items-center gap-1.5 text-xs font-medium"><AlertTriangle size={13} /> {t("fc.peakDay")}</p>
                  <p className="mt-1.5 font-display text-xl">{peak.date} · {peak.weekday}</p>
                  <p className="mt-1 text-sm">{peak.vehicles_needed} {t("fc.needed")} · {peak.utilisation_pct}%<Holidays drivers={peak.drivers} /></p>
                </div>
              )}
            </div>

            {/* By month */}
            {data.by_month?.length > 0 && (
              <section>
                <p className="label-eyebrow">{t("fc.byMonth")}</p>
                <div className="mt-4 space-y-3" data-testid="forecast-by-month">
                  {data.by_month.map((m) => (
                    <Bar key={m.month} pct={m.peak_utilisation_pct ?? m.avg_utilisation_pct} level={m.level} label={<span>{m.label}<Holidays drivers={m.drivers} /></span>} sub={`${m.peak_vehicles || m.avg_vehicles} ${t("fc.vehicles")}`} />
                  ))}
                </div>
              </section>
            )}

            {/* By week */}
            {data.by_week?.length > 0 && (
              <section>
                <p className="label-eyebrow">{t("fc.byWeek")}</p>
                <div className="mt-4 space-y-3" data-testid="forecast-by-week">
                  {data.by_week.slice(0, 12).map((w) => (
                    <Bar key={w.week_start} pct={w.avg_utilisation_pct} level={w.level} label={w.label} sub={`${w.peak_vehicles || w.avg_vehicles} ${t("fc.vehicles")}`} />
                  ))}
                </div>
              </section>
            )}

            {/* Day of week */}
            {data.by_dow?.length > 0 && (
              <section>
                <p className="label-eyebrow">{t("fc.byDow")}</p>
                <div className="mt-4 grid grid-cols-7 gap-2" data-testid="forecast-by-dow">
                  {data.by_dow.map((d) => {
                    const max = Math.max(...data.by_dow.map((x) => x.index));
                    const h = Math.max(12, Math.round((d.index / max) * 120));
                    return (
                      <div key={d.weekday} className="flex flex-col items-center gap-2">
                        <div className="flex h-32 items-end">
                          <div className="w-8 rounded-t bg-sea transition-all duration-700" style={{ height: `${h}px` }} title={`${d.index}`} />
                        </div>
                        <span className="text-xs text-slate-500">{d.short}</span>
                        <span className="text-[11px] font-semibold text-slate-700">{d.index}</span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
